import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  Skeleton,
} from "@mui/material";
import { MoreVert as MoreVertIcon, People as PeopleIcon } from "@mui/icons-material";
import { DataTableCard } from "../../components/organisms";
import { themedTableHeadCellSx, COLORS } from "../../styles/uiConstants";
import { useClinicTheme } from "../../theme";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

function getStatusChipSx(status) {
  switch (status) {
    case "Aktif":
      return { bgcolor: COLORS.successLight, color: COLORS.successDarker, fontWeight: 600 };
    case "Askıya Alındı":
      return { bgcolor: COLORS.warningLight, color: COLORS.warningDarker, fontWeight: 600 };
    case "Donduruldu":
      return { bgcolor: COLORS.catBlueLight, color: COLORS.catBlueDark, fontWeight: 600 };
    case "Devre Dışı":
      return { bgcolor: COLORS.errorLight, color: COLORS.errorDark, fontWeight: 600 };
    default:
      return { bgcolor: COLORS.slate100, color: COLORS.slate600, fontWeight: 600 };
  }
}

export default function StaffTable({
  pagedUsers,
  filteredCount,
  isLoading,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onMenuOpen,
  viewportHeight,
}) {
  const { palette } = useClinicTheme();
  const headCellSx = themedTableHeadCellSx(palette);

  const tableHeight = useMemo(() => {
    const rowH = 64,
      headH = 44;
    const desired = headH + rowsPerPage * rowH;
    const available = Math.max(260, (viewportHeight || 900) - 440);
    return Math.min(desired, available);
  }, [rowsPerPage, viewportHeight]);

  return (
    <DataTableCard
      loading={Boolean(isLoading)}
      tableHeight={tableHeight}
      empty={!isLoading && filteredCount === 0}
      emptyIcon={<PeopleIcon fontSize="inherit" />}
      emptyTitle="Personel bulunamadı."
      emptyMessage="Arama kriterlerinize uygun personel bulunamadı."
      page={page}
      rowsPerPage={rowsPerPage}
      totalCount={filteredCount}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>İsim</TableCell>
            <TableCell sx={headCellSx}>E-posta</TableCell>
            <TableCell sx={{ ...headCellSx, width: 200 }}>Ünvan</TableCell>
            <TableCell sx={{ ...headCellSx, width: 160 }}>Durum</TableCell>
            <TableCell align="center" sx={{ ...headCellSx, width: 80 }}>
              İşlemler
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            [...Array(Math.min(8, rowsPerPage))].map((_, i) => (
              <TableRow key={`sk-${i}`}>
                {Array.from({ length: 5 }).map((__, c) => (
                  <TableCell key={c}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : pagedUsers.length ? (
            pagedUsers.map((user, idx) => (
              <StaffRow
                key={user.id}
                user={user}
                idx={idx}
                palette={palette}
                onMenuOpen={onMenuOpen}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} sx={{ height: 1, py: 0 }} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </DataTableCard>
  );
}

function StaffRow({ user, idx, palette, onMenuOpen }) {
  const displayTitle = user.roleTemplate?.name || user.title || null;

  return (
    <TableRow
      hover
      sx={{
        background: idx % 2 === 0 ? COLORS.white : COLORS.slate50,
        transition: "all 0.2s ease",
        "&:hover": {
          background: `${palette.primary}04`,
          transform: "translateY(-1px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        },
        "&:last-child td": { borderBottom: "none" },
      }}
    >
      <TableCell sx={{ py: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              background: palette.primary,
              color: palette.contrastText,
              width: 40,
              height: 40,
              fontWeight: 700,
              fontSize: 15,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            {user.name?.charAt(0)}
          </Avatar>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
            {user.name} {user.surname}
          </Typography>
        </Box>
      </TableCell>

      <TableCell sx={{ py: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
          {user.email}
        </Typography>
      </TableCell>

      <TableCell sx={{ py: 2 }}>
        {displayTitle ? (
          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
            {displayTitle}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            &mdash;
          </Typography>
        )}
      </TableCell>

      <TableCell sx={{ py: 2 }}>
        <Chip
          label={user.accountStatus}
          size="small"
          sx={{ ...getStatusChipSx(user.accountStatus), borderRadius: 2, px: 1 }}
        />
      </TableCell>

      <TableCell align="center" sx={{ py: 2 }}>
        <IconButton
          size="small"
          onClick={(e) => onMenuOpen(e, user)}
          sx={{
            bgcolor: COLORS.slate50,
            "&:hover": {
              bgcolor: palette.primary,
              color: palette.contrastText,
              transform: "scale(1.1)",
            },
            transition: "all 0.2s ease",
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
